<script setup lang="ts">
  import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue'

  import MaintenanceBanner from '@/components/MaintenanceBanner.vue'
  import SaleBanner from '@/components/SaleBanner.vue'
  import GetYourOwnAcademyBanner from '@/components/GetYourOwnAcademyBanner.vue'
  import MainNavBar from '@/components/MainNavBar.vue'
  import EditorButtons from '@/components/editor/EditorButtons.vue'
  import TrialStatusBanner from '@/components/TrialStatusBanner.vue'
  import Sidebar from '@/components/Sidebar.vue'

  import useCommonMixin from '@/composables/useCommonMixin'
  import useCourse from '@/composables/useCourse'
  import useEditor from '@/composables/useEditor'
  import useBreakpoint from '@/composables/useBreakpoint'
  import { useRoute } from 'vue-router/composables'

  import { CourseModule } from '@/store/modules/course'

  const emit = defineEmits(['hide-widgets', 'show-widgets'])

  const route = useRoute()
  const { isSachkundeCompany, isKetchUpCompany, logoUrl, hasValidUser } = useCommonMixin()
  const { hasPurchasedAtLeastACourse, courseBundles } = useCourse()
  const { inEditorMode } = useEditor(route)
  const { isLargeDesktop } = useBreakpoint()
  const showGetYourOwnAcademyBanner = ref(false)
  const headerRef = ref<HTMLElement | null>(null)
  const resizeObserver = ref<ResizeObserver | null>(null)
  const isSidebarOpen = ref(false)
  const headerHeight = ref(0)
  const scrollY = ref(0)
  const resizeTimeout = ref<number | null>(null)

  const showMaintenanceBanner = computed(() => process.env.VUE_APP_MAINTENANCE_BANNER_VISIBLE === 'true')

  const showSaleBanner = computed(
    () =>
      process.env.VUE_APP_SALE_BANNER_VISIBLE === 'true' &&
      isSachkundeCompany.value &&
      hasValidUser.value &&
      courseBundles.value !== null &&
      !hasPurchasedAtLeastACourse.value,
  )

  const showTrialStatusBanner = computed(() => hasValidUser.value && CourseModule.course?.trial)

  const showMainNavBar = computed(() => {
    const isLoginOrResetPage = ['Login', 'ResetPassword'].includes(route.name as string)
    const showLogoIfPresent = isLoginOrResetPage ? logoUrl.value : true
    return isKetchUpCompany.value ? !isLoginOrResetPage && showLogoIfPresent : showLogoIfPresent
  })

  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value
  }

  const updateHeaderHeight = (entries: ResizeObserverEntry[]) => {
    if (resizeTimeout.value) {
      window.cancelAnimationFrame(resizeTimeout.value)
    }

    resizeTimeout.value = window.requestAnimationFrame(() => {
      if (entries[0]) {
        headerHeight.value = entries[0].contentRect.height
      }
    })
  }

  const handleScroll = () => {
    scrollY.value = window.scrollY
  }

  onMounted(() => {
    nextTick(() => {
      if (headerRef.value instanceof Element) {
        resizeObserver.value = new ResizeObserver(updateHeaderHeight)
        resizeObserver.value.observe(headerRef.value)
      }
    })
    window.addEventListener('scroll', handleScroll)
  })

  onUnmounted(() => {
    if (resizeObserver.value) {
      resizeObserver.value.disconnect()
    }
    if (resizeTimeout.value) {
      window.cancelAnimationFrame(resizeTimeout.value)
    }
    window.removeEventListener('scroll', handleScroll)
  })
</script>

<template lang="pug">
.app-header-wrapper(:style='{ height: `${headerHeight}px` }')
  .app-header(ref='headerRef', :class='{ "is-fixed": scrollY > 0 }')
    .banners-wrapper
      MaintenanceBanner(v-if='showMaintenanceBanner')
      SaleBanner(v-if='showSaleBanner')
      transition(mode='out-in', name='fade')
        GetYourOwnAcademyBanner(
          @hide-banner='showGetYourOwnAcademyBanner = false',
          v-if='showGetYourOwnAcademyBanner && hasPurchasedAtLeastACourse && false'
        )
      transition(mode='out-in', name='fade')
        TrialStatusBanner(v-if='showTrialStatusBanner')
    MainNavBar(
      v-if='showMainNavBar',
      @hide-widgets='emit("hide-widgets")',
      @show-widgets='emit("show-widgets")',
      @toggle-sidebar='toggleSidebar'
    )
    EditorButtons(ref='editorButtons', v-if='inEditorMode && isLargeDesktop && $route.name === "Dashboard"')
  Sidebar(:is-open='isSidebarOpen', @close-sidebar='isSidebarOpen = false')
</template>

<style lang="postcss">
  .app-header-wrapper {
    @apply ketch-relative ketch-w-screen;

    .app-header {
      @apply ketch-w-screen ketch-fixed ketch-left-0 ketch-top-0 ketch-z-[50];

      &.is-fixed {
        @apply ketch-bg-content-background-color ketch-shadow-md;
      }

      &.fluid {
        @apply ketch-relative;
      }
    }
  }
</style>
